import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Plus, Pencil, Trash2, Upload, FileText, Calculator, 
  TrendingUp, TrendingDown, Receipt, ChevronLeft, ChevronRight,
  Download, RefreshCw, Loader2, Image, FileImage
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString('tr-TR');
};

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const AccountingDocuments = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [invoices, setInvoices] = useState([]);
  const [sales, setSales] = useState([]);
  const [vatSummary, setVatSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [parsedResults, setParsedResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadType, setUploadType] = useState('invoice'); // 'invoice' veya 'sale'
  const fileInputRef = useRef(null);
  const saleFileInputRef = useRef(null);
  
  // Selection states for bulk operations
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedSales, setSelectedSales] = useState([]);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  
  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    description: '',
    amount: '',
    vat_amount: '',
    vat_rate: '20',
    total_amount: '',
    currency: 'TRY',
    invoice_no: '',
    document_type: 'fatura',
    notes: ''
  });
  
  const [saleForm, setSaleForm] = useState({
    date: new Date().toISOString().split('T')[0],
    customer: '',
    description: '',
    amount: '',
    vat_amount: '',
    vat_rate: '20',
    total_amount: '',
    withholding_rate: '',
    withholding_amount: '',
    invoice_no: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, salesRes, vatRes] = await Promise.all([
        axios.get(`${API}/accounting/invoices?month=${selectedMonth}`, { withCredentials: true }),
        axios.get(`${API}/accounting/sales?month=${selectedMonth}`, { withCredentials: true }),
        axios.get(`${API}/accounting/vat-summary?month=${selectedMonth}`, { withCredentials: true })
      ]);
      setInvoices(invoicesRes.data);
      setSales(salesRes.data);
      setVatSummary(vatRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-calculate VAT and total for invoice
  const handleInvoiceAmountChange = (field, value) => {
    const newForm = { ...invoiceForm, [field]: value };
    
    if (field === 'amount' || field === 'vat_rate') {
      const amount = parseFloat(newForm.amount) || 0;
      const vatRate = parseFloat(newForm.vat_rate) || 0;
      const vatAmount = amount * (vatRate / 100);
      newForm.vat_amount = vatAmount.toFixed(2);
      newForm.total_amount = (amount + vatAmount).toFixed(2);
    } else if (field === 'vat_amount') {
      const amount = parseFloat(newForm.amount) || 0;
      const vatAmount = parseFloat(value) || 0;
      newForm.total_amount = (amount + vatAmount).toFixed(2);
    }
    
    setInvoiceForm(newForm);
  };

  // Auto-calculate VAT and total for sale
  const handleSaleAmountChange = (field, value) => {
    const newForm = { ...saleForm, [field]: value };
    
    if (field === 'amount' || field === 'vat_rate') {
      const amount = parseFloat(newForm.amount) || 0;
      const vatRate = parseFloat(newForm.vat_rate) || 0;
      const vatAmount = amount * (vatRate / 100);
      newForm.vat_amount = vatAmount.toFixed(2);
      newForm.total_amount = (amount + vatAmount).toFixed(2);
    } else if (field === 'vat_amount') {
      const amount = parseFloat(newForm.amount) || 0;
      const vatAmount = parseFloat(value) || 0;
      newForm.total_amount = (amount + vatAmount).toFixed(2);
    }
    
    // Tevkifat hesapla
    if (field === 'withholding_rate' && value) {
      const [num, denom] = value.split('/').map(Number);
      if (num && denom) {
        const vatAmount = parseFloat(newForm.vat_amount) || 0;
        newForm.withholding_amount = (vatAmount * (num / denom)).toFixed(2);
      }
    }
    
    setSaleForm(newForm);
  };

  const openInvoiceDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setInvoiceForm({
        date: invoice.date,
        vendor: invoice.vendor,
        description: invoice.description || '',
        amount: invoice.amount?.toString() || '',
        vat_amount: invoice.vat_amount?.toString() || '',
        vat_rate: invoice.vat_rate?.toString() || '20',
        total_amount: invoice.total_amount?.toString() || '',
        currency: invoice.currency || 'TRY',
        invoice_no: invoice.invoice_no || '',
        document_type: invoice.document_type || 'fatura',
        notes: invoice.notes || ''
      });
    } else {
      setEditingInvoice(null);
      setInvoiceForm({
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        description: '',
        amount: '',
        vat_amount: '',
        vat_rate: '20',
        total_amount: '',
        currency: 'TRY',
        invoice_no: '',
        document_type: 'fatura',
        notes: ''
      });
    }
    setShowInvoiceDialog(true);
  };

  const openSaleDialog = (sale = null) => {
    if (sale) {
      setEditingSale(sale);
      setSaleForm({
        date: sale.date,
        customer: sale.customer,
        description: sale.description || '',
        amount: sale.amount?.toString() || '',
        vat_amount: sale.vat_amount?.toString() || '',
        vat_rate: sale.vat_rate?.toString() || '20',
        total_amount: sale.total_amount?.toString() || '',
        withholding_rate: sale.withholding_rate || '',
        withholding_amount: sale.withholding_amount?.toString() || '',
        invoice_no: sale.invoice_no || '',
        notes: sale.notes || ''
      });
    } else {
      setEditingSale(null);
      setSaleForm({
        date: new Date().toISOString().split('T')[0],
        customer: '',
        description: '',
        amount: '',
        vat_amount: '',
        vat_rate: '20',
        total_amount: '',
        withholding_rate: '',
        withholding_amount: '',
        invoice_no: '',
        notes: ''
      });
    }
    setShowSaleDialog(true);
  };

  const handleSaveInvoice = async () => {
    if (!invoiceForm.vendor || !invoiceForm.amount) {
      toast.error("Firma ve tutar zorunlu");
      return;
    }

    try {
      const payload = {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount),
        vat_amount: parseFloat(invoiceForm.vat_amount) || 0,
        vat_rate: parseFloat(invoiceForm.vat_rate) || 0,
        total_amount: parseFloat(invoiceForm.total_amount) || 0
      };

      if (editingInvoice) {
        await axios.put(`${API}/accounting/invoices/${editingInvoice.id}`, payload, { withCredentials: true });
        toast.success("Fatura güncellendi");
      } else {
        await axios.post(`${API}/accounting/invoices`, payload, { withCredentials: true });
        toast.success("Fatura eklendi");
      }

      setShowInvoiceDialog(false);
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleSaveSale = async () => {
    if (!saleForm.customer || !saleForm.amount) {
      toast.error("Müşteri ve tutar zorunlu");
      return;
    }

    try {
      const payload = {
        ...saleForm,
        amount: parseFloat(saleForm.amount),
        vat_amount: parseFloat(saleForm.vat_amount) || 0,
        vat_rate: parseFloat(saleForm.vat_rate) || 0,
        total_amount: parseFloat(saleForm.total_amount) || 0,
        withholding_amount: parseFloat(saleForm.withholding_amount) || null
      };

      if (editingSale) {
        await axios.put(`${API}/accounting/sales/${editingSale.id}`, payload, { withCredentials: true });
        toast.success("Fatura güncellendi");
      } else {
        await axios.post(`${API}/accounting/sales`, payload, { withCredentials: true });
        toast.success("Fatura eklendi");
      }

      setShowSaleDialog(false);
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Bu faturayı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/accounting/invoices/${id}`, { withCredentials: true });
      toast.success("Fatura silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const handleDeleteSale = async (id) => {
    if (!window.confirm("Bu faturayı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/accounting/sales/${id}`, { withCredentials: true });
      toast.success("Fatura silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  // File Upload Handlers
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    // Dosya türü kontrolü
    const allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png', '.html', '.htm'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedExtensions.includes(ext);
    });

    if (validFiles.length === 0) {
      toast.error("Desteklenmeyen dosya türü. PDF, JPEG, PNG veya HTML yükleyin.");
      return;
    }

    if (validFiles.length > 50) {
      toast.error("Maksimum 50 dosya yüklenebilir.");
      return;
    }

    setUploadedFiles(validFiles);
    setUploading(true);
    setParsedResults([]);
    setCurrentResultIndex(0);
    setUploadProgress({ current: 0, total: validFiles.length });
    setShowUploadDialog(true);

    // Dosyaları 10'arlık gruplar halinde gönder (chunked upload)
    const CHUNK_SIZE = 10;
    const allResults = [];
    
    for (let i = 0; i < validFiles.length; i += CHUNK_SIZE) {
      const chunk = validFiles.slice(i, i + CHUNK_SIZE);
      const formData = new FormData();
      chunk.forEach(file => formData.append('files', file));

      try {
        setUploadProgress({ current: i, total: validFiles.length, chunk: Math.floor(i / CHUNK_SIZE) + 1 });
        
        const response = await axios.post(`${API}/accounting/invoices/parse-batch`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 dakika timeout per chunk
        });

        if (response.data.results) {
          allResults.push(...response.data.results);
        }
      } catch (error) {
        console.error("Chunk upload error:", error);
        // Hatalı chunk için placeholder ekle
        chunk.forEach(file => {
          allResults.push({
            success: false,
            filename: file.name,
            error: error.response?.data?.detail || error.message || "Yükleme hatası"
          });
        });
      }
    }

    setParsedResults(allResults);
    setUploadProgress({ current: validFiles.length, total: validFiles.length });
    
    const successCount = allResults.filter(r => r.success).length;
    const failCount = allResults.length - successCount;
    
    if (successCount > 0 && failCount === 0) {
      toast.success(`${successCount} fatura başarıyla analiz edildi!`);
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`${successCount} başarılı, ${failCount} başarısız`);
    } else {
      toast.error("Hiçbir fatura analiz edilemedi");
    }
    
    setUploading(false);
  };

  const applyParsedData = (index = currentResultIndex) => {
    const result = parsedResults[index];
    if (!result?.success || !result?.parsed_data) return;
    
    const parsedData = result.parsed_data;
    
    if (uploadType === 'sale') {
      // Kesilen fatura için
      setSaleForm({
        date: parsedData.date || new Date().toISOString().split('T')[0],
        customer: parsedData.vendor || '', // vendor -> customer
        description: parsedData.description || '',
        amount: parsedData.amount?.toString() || '',
        vat_amount: parsedData.vat_amount?.toString() || '',
        vat_rate: parsedData.vat_rate?.toString() || '20',
        total_amount: parsedData.total_amount?.toString() || '',
        withholding_rate: '',
        withholding_amount: '',
        invoice_no: parsedData.invoice_no || '',
        notes: ''
      });
      
      // Bu sonucu listeden kaldır
      const newResults = parsedResults.filter((_, i) => i !== index);
      setParsedResults(newResults);
      
      if (newResults.length === 0) {
        setShowUploadDialog(false);
      } else {
        setCurrentResultIndex(Math.min(index, newResults.length - 1));
      }
      
      setShowSaleDialog(true);
      toast.success("Veriler forma aktarıldı");
    } else {
      // Gider faturası için
      setInvoiceForm({
        date: parsedData.date || new Date().toISOString().split('T')[0],
        vendor: parsedData.vendor || '',
        description: parsedData.description || '',
        amount: parsedData.amount?.toString() || '',
        vat_amount: parsedData.vat_amount?.toString() || '',
        vat_rate: parsedData.vat_rate?.toString() || '20',
        total_amount: parsedData.total_amount?.toString() || '',
        currency: 'TRY',
        invoice_no: parsedData.invoice_no || '',
        document_type: 'fatura',
        notes: ''
      });
      
      // Bu sonucu listeden kaldır
      const newResults = parsedResults.filter((_, i) => i !== index);
      setParsedResults(newResults);
      
      if (newResults.length === 0) {
        setShowUploadDialog(false);
      } else {
        setCurrentResultIndex(Math.min(index, newResults.length - 1));
      }
      
      setShowInvoiceDialog(true);
      toast.success("Veriler forma aktarıldı");
    }
  };

  const skipCurrentResult = () => {
    if (currentResultIndex < parsedResults.length - 1) {
      setCurrentResultIndex(currentResultIndex + 1);
    }
  };

  const prevResult = () => {
    if (currentResultIndex > 0) {
      setCurrentResultIndex(currentResultIndex - 1);
    }
  };

  const resetUpload = () => {
    setUploadedFiles([]);
    setParsedResults([]);
    setCurrentResultIndex(0);
    setShowUploadDialog(false);
    setUploadType('invoice');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (saleFileInputRef.current) {
      saleFileInputRef.current.value = '';
    }
  };

  // Toplu silme işlemleri
  const handleBulkDeleteInvoices = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Silinecek fatura seçin");
      return;
    }
    
    if (!window.confirm(`${selectedInvoices.length} faturayı silmek istediğinize emin misiniz?`)) return;
    
    try {
      await axios.post(`${API}/accounting/invoices/bulk-delete`, 
        { ids: selectedInvoices }, 
        { withCredentials: true }
      );
      toast.success(`${selectedInvoices.length} fatura silindi`);
      setSelectedInvoices([]);
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleBulkDeleteSales = async () => {
    if (selectedSales.length === 0) {
      toast.error("Silinecek fatura seçin");
      return;
    }
    
    if (!window.confirm(`${selectedSales.length} faturayı silmek istediğinize emin misiniz?`)) return;
    
    try {
      await axios.post(`${API}/accounting/sales/bulk-delete`, 
        { ids: selectedSales }, 
        { withCredentials: true }
      );
      toast.success(`${selectedSales.length} fatura silindi`);
      setSelectedSales([]);
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const toggleInvoiceSelection = (id) => {
    setSelectedInvoices(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSaleSelection = (id) => {
    setSelectedSales(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllInvoices = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  const selectAllSales = () => {
    if (selectedSales.length === sales.length) {
      setSelectedSales([]);
    } else {
      setSelectedSales(sales.map(s => s.id));
    }
  };

  // Kesilen faturalar için dosya yükleme
  const handleSaleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setUploadType('sale');
      await processFiles(files);
    }
  };

  const openSaleUpload = () => {
    setUploadType('sale');
    saleFileInputRef.current?.click();
  };

  // Excel Export
  const handleExportExcel = async () => {
    try {
      toast.info("Excel dosyası hazırlanıyor...");
      const response = await axios.get(`${API}/accounting/export-excel?month=${selectedMonth}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Dosyayı indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Muhasebe_Raporu_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel dosyası indirildi!");
    } catch (error) {
      toast.error("Excel indirme hatası");
      console.error(error);
    }
  };

  const changeMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + direction;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const getMonthName = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return `${MONTHS_TR[month - 1]} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-rose-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-zinc-50">Muhasebe Evrakları</h2>
          <p className="text-zinc-400 text-sm">Aylık gider ve satış faturaları</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Hidden file input for Gider Faturaları */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.html,.htm"
            multiple
            onChange={(e) => { setUploadType('invoice'); handleFileSelect(e); }}
            className="hidden"
          />
          
          {/* Hidden file input for Kesilen Faturalar */}
          <input
            ref={saleFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.html,.htm"
            multiple
            onChange={handleSaleFileSelect}
            className="hidden"
          />
          
          {/* Upload Button for Gider */}
          <Button
            variant="outline"
            onClick={() => { setUploadType('invoice'); fileInputRef.current?.click(); }}
            className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
            data-testid="upload-invoice-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Gider Yükle
          </Button>
          
          {/* Excel Export Button */}
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => changeMonth(-1)}
            className="border-zinc-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-semibold text-zinc-50 min-w-[140px] text-center">
            {getMonthName()}
          </span>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => changeMonth(1)}
            className="border-zinc-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchData}
            className="border-zinc-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KDV Özeti */}
      {vatSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-rose-400" />
              <span className="text-sm text-rose-400/80">Ödenen KDV</span>
            </div>
            <p className="text-2xl font-bold text-rose-400">
              {formatCurrency(vatSummary.vat_summary?.paid_vat)}
            </p>
            <p className="text-xs text-zinc-500 mt-1">{vatSummary.invoices?.count || 0} fatura</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-400/80">Tahsil Edilen KDV</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {formatCurrency(vatSummary.vat_summary?.collected_vat)}
            </p>
            <p className="text-xs text-zinc-500 mt-1">{vatSummary.sales?.count || 0} fatura</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400/80">Tevkifat</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {formatCurrency(vatSummary.vat_summary?.withholding)}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`border rounded-xl p-4 ${
              vatSummary.vat_summary?.payable_vat >= 0 
                ? 'bg-violet-500/10 border-violet-500/20' 
                : 'bg-teal-500/10 border-teal-500/20'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Calculator className={`w-4 h-4 ${vatSummary.vat_summary?.payable_vat >= 0 ? 'text-violet-400' : 'text-teal-400'}`} />
              <span className={`text-sm ${vatSummary.vat_summary?.payable_vat >= 0 ? 'text-violet-400/80' : 'text-teal-400/80'}`}>
                {vatSummary.vat_summary?.payable_vat >= 0 ? 'Ödenecek KDV' : 'Devreden KDV'}
              </span>
            </div>
            <p className={`text-2xl font-bold ${vatSummary.vat_summary?.payable_vat >= 0 ? 'text-violet-400' : 'text-teal-400'}`}>
              {formatCurrency(Math.abs(vatSummary.vat_summary?.payable_vat || 0))}
            </p>
          </motion.div>
        </div>
      )}

      {/* İki Sütunlu Tablo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SOL - GİDER FATURALARI */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              <span className="font-bold">Gider Faturaları (Alınan)</span>
              {selectedInvoices.length > 0 && (
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                  {selectedInvoices.length} seçili
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedInvoices.length > 0 && (
                <Button 
                  size="sm"
                  onClick={handleBulkDeleteInvoices}
                  className="bg-white/20 hover:bg-white/30 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Sil ({selectedInvoices.length})
                </Button>
              )}
              <Button 
                size="sm"
                onClick={() => openInvoiceDialog()}
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Ekle
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50">
                <tr>
                  <th className="w-10 py-2 px-2">
                    <input 
                      type="checkbox"
                      checked={invoices.length > 0 && selectedInvoices.length === invoices.length}
                      onChange={selectAllInvoices}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-rose-500 focus:ring-rose-500"
                    />
                  </th>
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium">Tarih</th>
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium">Firma</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium">Tutar</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium">KDV</th>
                  <th className="text-center py-2 px-3 text-zinc-400 font-medium">%</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {invoices.length > 0 ? (
                  invoices.map((inv) => (
                    <tr 
                      key={inv.id} 
                      className={`hover:bg-zinc-800/30 cursor-pointer ${selectedInvoices.includes(inv.id) ? 'bg-rose-500/10' : ''}`}
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox"
                          checked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoiceSelection(inv.id)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-rose-500 focus:ring-rose-500"
                        />
                      </td>
                      <td className="py-2 px-3 text-zinc-400" onClick={() => openInvoiceDialog(inv)}>{formatDate(inv.date)}</td>
                      <td className="py-2 px-3 text-zinc-200" onClick={() => openInvoiceDialog(inv)}>{inv.vendor}</td>
                      <td className="py-2 px-3 text-right text-rose-400 font-mono" onClick={() => openInvoiceDialog(inv)}>
                        {formatCurrency(inv.total_amount)}
                      </td>
                      <td className="py-2 px-3 text-right text-zinc-400 font-mono" onClick={() => openInvoiceDialog(inv)}>
                        {formatCurrency(inv.vat_amount)}
                      </td>
                      <td className="py-2 px-3 text-center text-zinc-500" onClick={() => openInvoiceDialog(inv)}>%{inv.vat_rate}</td>
                      <td className="py-2 px-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-400 hover:bg-rose-400/10"
                          onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(inv.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-zinc-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p>Bu ay için fatura yok</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Toplam */}
          <div className="bg-rose-500/10 px-4 py-3 flex justify-between items-center border-t border-zinc-800">
            <span className="text-rose-400 font-semibold">TOPLAM</span>
            <span className="font-mono font-bold text-rose-400">
              {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0))}
            </span>
          </div>
        </div>

        {/* SAĞ - KESİLEN FATURALAR */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              <span className="font-bold">Kesilen Faturalar (Satış)</span>
              {selectedSales.length > 0 && (
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                  {selectedSales.length} seçili
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedSales.length > 0 && (
                <Button 
                  size="sm"
                  onClick={handleBulkDeleteSales}
                  className="bg-white/20 hover:bg-white/30 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Sil ({selectedSales.length})
                </Button>
              )}
              <Button 
                size="sm"
                onClick={openSaleUpload}
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <Upload className="w-4 h-4 mr-1" /> Yükle
              </Button>
              <Button 
                size="sm"
                onClick={() => openSaleDialog()}
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Ekle
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50">
                <tr>
                  <th className="w-10 py-2 px-2">
                    <input 
                      type="checkbox"
                      checked={sales.length > 0 && selectedSales.length === sales.length}
                      onChange={selectAllSales}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                    />
                  </th>
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium">Tarih</th>
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium">Müşteri</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium">Tutar</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium">KDV</th>
                  <th className="text-center py-2 px-3 text-zinc-400 font-medium">Tevkifat</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {sales.length > 0 ? (
                  sales.map((sale) => (
                    <tr 
                      key={sale.id} 
                      className={`hover:bg-zinc-800/30 cursor-pointer ${selectedSales.includes(sale.id) ? 'bg-emerald-500/10' : ''}`}
                      data-testid={`sale-row-${sale.id}`}
                    >
                      <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox"
                          checked={selectedSales.includes(sale.id)}
                          onChange={() => toggleSaleSelection(sale.id)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="py-2 px-3 text-zinc-400" onClick={() => openSaleDialog(sale)}>{formatDate(sale.date)}</td>
                      <td className="py-2 px-3 text-zinc-200" onClick={() => openSaleDialog(sale)}>{sale.customer}</td>
                      <td className="py-2 px-3 text-right text-emerald-400 font-mono" onClick={() => openSaleDialog(sale)}>
                        {formatCurrency(sale.total_amount)}
                      </td>
                      <td className="py-2 px-3 text-right text-zinc-400 font-mono" onClick={() => openSaleDialog(sale)}>
                        {formatCurrency(sale.vat_amount)}
                      </td>
                      <td className="py-2 px-3 text-center text-amber-400" onClick={() => openSaleDialog(sale)}>
                        {sale.withholding_rate || '-'}
                      </td>
                      <td className="py-2 px-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-400 hover:bg-rose-400/10"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSale(sale.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-zinc-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p>Bu ay için fatura yok</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Toplam */}
          <div className="bg-emerald-500/10 px-4 py-3 flex justify-between items-center border-t border-zinc-800">
            <span className="text-emerald-400 font-semibold">TOPLAM</span>
            <span className="font-mono font-bold text-emerald-400">
              {formatCurrency(sales.reduce((sum, s) => sum + (s.total_amount || 0), 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Gider Faturası Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-rose-400" />
              {editingInvoice ? 'Gider Faturası Düzenle' : 'Yeni Gider Faturası'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Tarih *</Label>
                <Input 
                  type="date"
                  value={invoiceForm.date}
                  onChange={(e) => setInvoiceForm({...invoiceForm, date: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Fatura No</Label>
                <Input 
                  value={invoiceForm.invoice_no}
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_no: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="Opsiyonel"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Firma Adı *</Label>
              <Input 
                value={invoiceForm.vendor}
                onChange={(e) => setInvoiceForm({...invoiceForm, vendor: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-700"
                placeholder="Ör: ABC Ltd. Şti."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Tutar (KDV Hariç) *</Label>
                <Input 
                  type="number"
                  value={invoiceForm.amount}
                  onChange={(e) => handleInvoiceAmountChange('amount', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-zinc-400">KDV Oranı (%)</Label>
                <Input 
                  type="number"
                  value={invoiceForm.vat_rate}
                  onChange={(e) => handleInvoiceAmountChange('vat_rate', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="20"
                />
              </div>
              <div>
                <Label className="text-zinc-400">KDV Tutarı</Label>
                <Input 
                  type="number"
                  value={invoiceForm.vat_amount}
                  onChange={(e) => handleInvoiceAmountChange('vat_amount', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-rose-400 font-medium">TOPLAM TUTAR</span>
                <span className="text-xl font-bold text-rose-400">
                  {formatCurrency(parseFloat(invoiceForm.total_amount) || 0)}
                </span>
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Notlar</Label>
              <Textarea 
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-700"
                placeholder="Opsiyonel notlar..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)} className="border-zinc-700">
              İptal
            </Button>
            <Button onClick={handleSaveInvoice} className="bg-rose-500 hover:bg-rose-400">
              {editingInvoice ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kesilen Fatura Dialog */}
      <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              {editingSale ? 'Kesilen Fatura Düzenle' : 'Yeni Kesilen Fatura'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Tarih *</Label>
                <Input 
                  type="date"
                  value={saleForm.date}
                  onChange={(e) => setSaleForm({...saleForm, date: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Fatura No</Label>
                <Input 
                  value={saleForm.invoice_no}
                  onChange={(e) => setSaleForm({...saleForm, invoice_no: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="Opsiyonel"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Müşteri Adı *</Label>
              <Input 
                value={saleForm.customer}
                onChange={(e) => setSaleForm({...saleForm, customer: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-700"
                placeholder="Ör: XYZ A.Ş."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Tutar (KDV Hariç) *</Label>
                <Input 
                  type="number"
                  value={saleForm.amount}
                  onChange={(e) => handleSaleAmountChange('amount', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-zinc-400">KDV Oranı (%)</Label>
                <Input 
                  type="number"
                  value={saleForm.vat_rate}
                  onChange={(e) => handleSaleAmountChange('vat_rate', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="20"
                />
              </div>
              <div>
                <Label className="text-zinc-400">KDV Tutarı</Label>
                <Input 
                  type="number"
                  value={saleForm.vat_amount}
                  onChange={(e) => handleSaleAmountChange('vat_amount', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Tevkifat Oranı</Label>
                <Input 
                  value={saleForm.withholding_rate}
                  onChange={(e) => handleSaleAmountChange('withholding_rate', e.target.value)}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="Ör: 5/10"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Tevkifat Tutarı</Label>
                <Input 
                  type="number"
                  value={saleForm.withholding_amount}
                  onChange={(e) => setSaleForm({...saleForm, withholding_amount: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-700"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-emerald-400 font-medium">TOPLAM TUTAR</span>
                <span className="text-xl font-bold text-emerald-400">
                  {formatCurrency(parseFloat(saleForm.total_amount) || 0)}
                </span>
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Notlar</Label>
              <Textarea 
                value={saleForm.notes}
                onChange={(e) => setSaleForm({...saleForm, notes: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-700"
                placeholder="Opsiyonel notlar..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaleDialog(false)} className="border-zinc-700">
              İptal
            </Button>
            <Button onClick={handleSaveSale} className="bg-emerald-500 hover:bg-emerald-400">
              {editingSale ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => !open && resetUpload()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Upload className={`w-5 h-5 ${uploadType === 'sale' ? 'text-emerald-400' : 'text-violet-400'}`} />
              {uploadType === 'sale' ? 'Kesilen Fatura Yükle' : 'Gider Faturası Yükle'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              PDF, JPEG, PNG veya HTML faturaları yükleyin. Maks 50 dosya (10'ar grup halinde işlenir).
            </DialogDescription>
          </DialogHeader>

          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
              isDragging 
                ? 'border-violet-400 bg-violet-500/10' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="upload-dropzone"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-12 h-12 text-violet-400 animate-spin" />
                <p className="text-zinc-400">
                  {uploadProgress.current}/{uploadProgress.total} fatura işleniyor...
                </p>
                {uploadProgress.chunk && (
                  <p className="text-xs text-zinc-500">
                    Grup {uploadProgress.chunk} analiz ediliyor (10'ar dosya)
                  </p>
                )}
                <div className="w-full max-w-xs bg-zinc-800 rounded-full h-2">
                  <div 
                    className="bg-violet-500 h-2 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            ) : parsedResults.length > 0 ? (
              <div className="space-y-4">
                {/* Progress Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-400">
                      Fatura {currentResultIndex + 1} / {parsedResults.length}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      parsedResults[currentResultIndex]?.success 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-rose-500/20 text-rose-400'
                    }`}>
                      {parsedResults[currentResultIndex]?.success ? 'Başarılı' : 'Hatalı'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={prevResult}
                      disabled={currentResultIndex === 0}
                      className="h-7 w-7"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={skipCurrentResult}
                      disabled={currentResultIndex >= parsedResults.length - 1}
                      className="h-7 w-7"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Current File Info */}
                <div className="flex items-center justify-center gap-2 text-violet-400">
                  <FileText className="w-6 h-6" />
                  <span className="font-medium text-sm truncate max-w-[300px]">
                    {parsedResults[currentResultIndex]?.filename}
                  </span>
                </div>
                
                {parsedResults[currentResultIndex]?.success ? (
                  /* Parsed Data Preview */
                  <div className="bg-zinc-950 rounded-lg p-4 text-left space-y-2">
                    <h4 className="text-sm font-semibold text-violet-400 mb-3">Çıkarılan Veriler:</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-zinc-500">Firma:</span>
                        <p className="text-zinc-200">{parsedResults[currentResultIndex]?.parsed_data?.vendor || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Tarih:</span>
                        <p className="text-zinc-200">{parsedResults[currentResultIndex]?.parsed_data?.date || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Fatura No:</span>
                        <p className="text-zinc-200">{parsedResults[currentResultIndex]?.parsed_data?.invoice_no || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">KDV Oranı:</span>
                        <p className="text-zinc-200">%{parsedResults[currentResultIndex]?.parsed_data?.vat_rate || '-'}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">Tutar (KDV Hariç):</span>
                        <p className="text-zinc-200">{formatCurrency(parsedResults[currentResultIndex]?.parsed_data?.amount)}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500">KDV Tutarı:</span>
                        <p className="text-zinc-200">{formatCurrency(parsedResults[currentResultIndex]?.parsed_data?.vat_amount)}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-zinc-800">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 font-medium">TOPLAM:</span>
                        <span className="text-lg font-bold text-violet-400">
                          {formatCurrency(parsedResults[currentResultIndex]?.parsed_data?.total_amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Error Display */
                  <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 text-left">
                    <p className="text-rose-400 text-sm">
                      {parsedResults[currentResultIndex]?.error || 'Bu fatura analiz edilemedi'}
                    </p>
                  </div>
                )}

                {/* Summary Bar */}
                <div className="flex items-center justify-center gap-4 pt-2 border-t border-zinc-800">
                  <span className="text-xs text-emerald-400">
                    ✓ {parsedResults.filter(r => r.success).length} başarılı
                  </span>
                  <span className="text-xs text-rose-400">
                    ✗ {parsedResults.filter(r => !r.success).length} başarısız
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center">
                  <FileImage className="w-8 h-8 text-violet-400" />
                </div>
                <div>
                  <p className="text-zinc-300 font-medium">Dosyaları sürükleyip bırakın</p>
                  <p className="text-sm text-zinc-500">veya tıklayarak seçin (çoklu seçim desteklenir)</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-violet-500/50 text-violet-400"
                >
                  Dosya Seç
                </Button>
                <p className="text-xs text-zinc-600">PDF, JPEG, PNG, HTML (maks. 50 dosya)</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={resetUpload} 
              className="border-zinc-700"
            >
              {parsedResults.length > 0 ? 'Kapat' : 'İptal'}
            </Button>
            {parsedResults.length > 0 && parsedResults[currentResultIndex]?.success && (
              <Button 
                onClick={() => applyParsedData(currentResultIndex)} 
                className="bg-violet-500 hover:bg-violet-400"
                data-testid="apply-parsed-data-btn"
              >
                Forma Aktar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingDocuments;
